import React, { useContext, useEffect, useRef } from "react";
import { getAnalyticsBotSource } from "utils/misc";
import Text from "common/Typography/Text";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import styled, { useTheme } from "styled-components";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import useQuery from "utils/hooks/useQuery";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { LARGE_MOBILE, MOBILE_SEARCH } from "common/constants";
import { DarkEmail, Email } from "common/Icons/Icons";
import { AuthDescription, PrimaryTitle, Wrapper } from "features/auth/styles";
import AuthInlineError, {
  useAuthResultMessage,
} from "features/auth/AuthInlineError";
import LinkButton from "common/Buttons/LinkButton";
import useMedia from "utils/hooks/useMedia";
import VerificationCode from "features/auth/VerificationCode";
import ResendEmailInstructions from "features/auth/ResendEmailInstructions";
import { OnboardingStep } from "analytics/events";
import { queueVerificationAnalytics } from "analytics/client";
import {
  EMAIL_UUID_QUERY_PARAM,
  REGISTERED_EMAIL,
} from "@shared/common/constants";
import useVerifyEmail from "features/auth/hooks/useVerifyEmail";
import Link from "common/Typography/Link";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { AUTH_VERIFICATION_EMAIL_ID, AUTH_RESEND_EMAIL_ID } from "testIds";

const VerifyEmail = () => {
  const { analytics, deferredAnalytics } = useContext(AnalyticsContext);
  const hasTrackedView = useRef(false);
  const appTheme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { appendQuery } = useHistoryWithQuery();
  const query = useQuery();
  const emailValidatorUUID = query.get(EMAIL_UUID_QUERY_PARAM);
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const { display: displayResultMessage, error } = useAuthResultMessage();

  const {
    onResendCode,
    onSubmit,
    showSupportLink,
    codeText,
    resendLoading,
    verificationCodeLoading,
  } = useVerifyEmail({
    emailValidatorUUID,
    displayResultMessage,
    onSubmitSuccess: () => {
      appendQuery(undefined, [EMAIL_UUID_QUERY_PARAM]);
    },
    onResendSuccess: (emailValidatorUUID: string) => {
      appendQuery({
        [EMAIL_UUID_QUERY_PARAM]: emailValidatorUUID,
      });
    },
    storageEmail: sessionStorage.getItem(REGISTERED_EMAIL),
  });

  useEffect(() => {
    if (hasTrackedView.current) return;
    const userSource = getAnalyticsBotSource();
    const properties = {
      botSource: userSource,
      onboardingStep: OnboardingStep.EMAIL_CONFIRMATION,
    };
    if (emailValidatorUUID && deferredAnalytics) {
      hasTrackedView.current = true;
      queueVerificationAnalytics(emailValidatorUUID, "view", () =>
        deferredAnalytics.onboardingVerifyEmailView(properties)
      );
    } else if (!emailValidatorUUID && analytics) {
      hasTrackedView.current = true;
      analytics.onboardingVerifyEmailView(properties);
    }
  }, [analytics, deferredAnalytics, emailValidatorUUID]);

  const Icon = appTheme.isDarkMode ? DarkEmail : Email;
  const resend = (): void => {
    onResendCode();
    const properties = { onboardingStep: OnboardingStep.EMAIL_CONFIRMATION };
    if (emailValidatorUUID)
      queueVerificationAnalytics(emailValidatorUUID, "resend", () =>
        deferredAnalytics?.registrationSendVerifyEmailAgainClicked(properties)
      );
    else analytics?.registrationSendVerifyEmailAgainClicked(properties);
  };
  return (
    <Wrapper $isMobile={isMobile}>
      <LoadingSpinnerOverlay
        isLoading={resendLoading && !appTheme.isNewLook}
        backgroundColor={
          appTheme.isDarkMode
            ? appTheme.colors.layoutBgColor
            : appTheme.colors.cardBgColor
        }
      >
        {appTheme.isNewLook ? (
          <>
            <PrimaryTitle $isMobile={isMobile}>
              {t("verify_email.title_new_look")}
            </PrimaryTitle>
            <AuthDescription data-testid={AUTH_VERIFICATION_EMAIL_ID}>
              {codeText}
            </AuthDescription>
          </>
        ) : (
          <>
            <PrimaryTitle $isMobile={isMobile}>
              {t("verify_email.title")}
            </PrimaryTitle>
            <Icon style={{ marginBottom: 24, width: 120, height: 120 }} />
            <Text data-testid={AUTH_VERIFICATION_EMAIL_ID}>{codeText}</Text>
          </>
        )}
        <VerificationCode
          numOfDigits={NUM_OF_DIGITS_CODE}
          title={t(
            appTheme.isNewLook
              ? "verify_email.verification_code_new_look"
              : "verify_email.verification_code"
          )}
          onSubmit={onSubmit}
          isLoading={verificationCodeLoading}
          additionalItems={
            error ? (
              <AuthInlineError message={error} style={{ margin: "16px 0 0" }} />
            ) : undefined
          }
        />
        {appTheme.isNewLook ? (
          <ResendRow>
            <LinkButton
              type="link"
              loading={resendLoading}
              data-testid={AUTH_RESEND_EMAIL_ID}
              onClick={resend}
            >
              {t("verify_email.resend_email_new_look")}
            </LinkButton>
          </ResendRow>
        ) : (
          <Link data-testid={AUTH_RESEND_EMAIL_ID} onClick={resend}>
            {t("verify_email.resend_email")}
          </Link>
        )}
        {showSupportLink && <ResendEmailInstructions isVerify={true} />}
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default VerifyEmail;

const NUM_OF_DIGITS_CODE = 6;

// New look: the resend link under the action, centred, loading in place.
const ResendRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;
  .ant-btn-link {
    height: auto;
    padding: 4px 8px;
  }
  /* Phones: a thumb-sized hit area, same visual position. */
  @media (max-width: ${LARGE_MOBILE}) {
    margin-top: 4px;
    .ant-btn-link {
      padding: 12px 16px;
    }
  }
`;
